import { useState, useMemo, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, Legend, LinearScale, CategoryScale, PointElement, LineElement, Filler } from 'chart.js';
import { Panel, Select } from '@crazyegginc/hatch';

import { SkeletonChart } from '/src/components/SkeletonChart';
import { useChartTooltip, useTimeZonedFunctions } from '/src/hooks';
import { useFunnelEditorContext } from '../../funnel-editor-context';
import { METRICS } from '../../constants';
import { abbreviateNumber, formatCurrency } from '/src/utils';
import { ChartTooltipArrow } from '/src/components/ChartTooltipArrow';
import { round } from '/src/utils/math';

ChartJS.defaults.font.family = 'Proxima Nova, Lucida Grande, Tahoma, Verdana, Arial, sans-serif';
ChartJS.defaults.font.size = 14;

ChartJS.register(Legend, LinearScale, CategoryScale, PointElement, LineElement, Filler);

export function TimeChart({ loading, stepsData, timeUnit, timeUnitResults, currency }) {
  const { selectedStep } = useFunnelEditorContext();
  const [metric, setMetric] = useState(METRICS.VISITORS);
  const { tooltip, external } = useChartTooltip();
  const { formatChartLabel, formatChartTooltip } = useTimeZonedFunctions();
  const isLastStep = selectedStep === stepsData.length - 1;

  const metricOptions = useMemo(
    () =>
      Object.values(METRICS)
        .filter((m) => ![METRICS.CONVERSIONS, METRICS.CONVERSION_RATE].includes(m) || !isLastStep)
        .map((value) => ({ value, label: value.replace('_CURRENCY_', currency) })),
    [currency, isLastStep],
  );

  useEffect(() => {
    if (!metricOptions.map((m) => m.value).includes(metric)) {
      setMetric(METRICS.VISITORS);
    }
  }, [metricOptions, metric]);

  const labels = useMemo(() => {
    if (!timeUnitResults.length) return null;
    return timeUnitResults.map((x) => formatChartLabel(x.startAt, timeUnit));
  }, [timeUnitResults, timeUnit, formatChartLabel]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          data: timeUnitResults.map((r) => {
            switch (metric) {
              case METRICS.VISITORS:
                return r.steps[selectedStep];
              case METRICS.CONVERSIONS:
                return isLastStep ? 0 : r.steps[selectedStep + 1];
              case METRICS.CONVERSION_RATE:
                return isLastStep ? 0 : r.conversionRates[selectedStep];
              case METRICS.AVG_VALUE: {
                const visits = r.steps[selectedStep];
                let v = visits ? r.values[selectedStep].displayWorth / 10000 / visits : 0;
                v = Number.isFinite(Number(v)) ? v / 1 : 0;
                return v;
              }
              case METRICS.TOTAL_VALUE: {
                let v = r.values[selectedStep].displayWorth / 10000;
                v = Number.isFinite(Number(v)) ? v / 1 : 0;
                return v;
              }
            }
          }),
          borderColor: '#A85CD0',
          backgroundColor: (ctx) => {
            const chart = ctx.chart;
            const { ctx: canvasCtx, chartArea } = chart;
            if (!chartArea) {
              // initial render
              return null;
            }
            const gradient = canvasCtx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, 'rgba(168, 92, 208, 0.2)');
            gradient.addColorStop(1, 'rgba(168, 92, 208, 0)');
            return gradient;
          },
          fill: true,

          pointRadius: 0,
          pointHoverRadius: 5,
          pointBorderWidth: 2,
          pointBorderColor: 'rgba(255,255,255,0)',
          pointHoverBorderColor: 'rgb(255,255,255)',
          pointStyle: 'circle',
          pointBackgroundColor: '#A85CD0',
          label: 'Everyone',
          clip: false,
        },
      ],
    }),
    [timeUnitResults, metric, labels, selectedStep, isLastStep],
  );

  const maxValue = useMemo(() => (data.datasets[0].data.length === 0 ? 0 : Math.max(...data.datasets[0].data)), [data]);
  const chartSuggestedMax = useMemo(
    () => (maxValue === 0 ? (metric === METRICS.CONVERSION_RATE ? 100 : 1000) : Math.ceil(maxValue * 1.1)),
    [maxValue, metric],
  );

  const options = useMemo(() => {
    return {
      responsive: true,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          suggestedMax: chartSuggestedMax,
          ticks: {
            color: 'rgb(104, 118, 141)',
            padding: 10,
            maxTicksLimit: 5,
            callback: (value) =>
              metric === METRICS.CONVERSION_RATE ? `${value}%` : abbreviateNumber(value).toLowerCase(),
          },
          grid: {
            color: 'rgba(151, 151, 151, 0.2)',
            drawTicks: false,
          },
          border: {
            display: false,
          },
          grace: 0,
        },
        x: {
          type: 'category',
          grid: { display: false },
          border: { display: false },
          ticks: { color: 'rgb(104, 118, 141)', align: 'inner', maxTicksLimit: 10 },
        },
      },
      plugins: {
        tooltip: {
          enabled: false,
          external,
        },
        legend: {
          display: false,
        },
      },
      layout: {
        padding: 0,
      },
    };
  }, [external, metric, chartSuggestedMax]);

  if (timeUnitResults.length <= 1) return null;

  return (
    <Panel variant="secondary" className="mt-5">
      <div className="mb-6 flex items-center justify-between w-full">
        <div className="flex items-center space-x-2.5">
          <div className="flex items-center justify-center w-6 h-6 rounded bg-cadet-blue-500 text-white text-header-4">
            {selectedStep + 1}
          </div>
          <span className="text-header-2">{stepsData[selectedStep]?.name}</span>
        </div>
        <div className="w-[180px]">
          <Select
            options={metricOptions}
            value={metric}
            onChange={setMetric}
            style={{
              background: '#e5f2fc',
              borderColor: '#e5f2fc',
              cursor: 'pointer',
              color: '#0086e6',
              fontWeight: 'bold',
            }}
          />
        </div>
      </div>
      <div className="h-[260px] w-full relative">
        {loading ? <SkeletonChart /> : <Line data={data} options={options} />}
        <ChartTooltip
          tooltip={tooltip}
          label={
            timeUnitResults[tooltip?.dataIndex]
              ? formatChartTooltip(
                  timeUnitResults[tooltip.dataIndex].startAt,
                  timeUnitResults[tooltip.dataIndex].endAt,
                  timeUnit,
                )
              : ''
          }
          valueFormatter={getValueFormatter(metric, currency)}
        />
      </div>
    </Panel>
  );
}

function ChartTooltip({ tooltip, label, valueFormatter }) {
  if (!tooltip || !tooltip.visible) return null;
  const { anchorX, anchorY, attachSide, items = [], transform, offsetXMultiplier, offsetYMultiplier } = tooltip;

  const GAP = 15; // distance from tooltip to chart point

  let offsetX = offsetXMultiplier * GAP;
  let offsetY = offsetYMultiplier * GAP;

  return (
    <div
      role="tooltip"
      className="absolute pointer-events-none w-[150px] bg-woodsmoke-500/90 rounded text-white p-2.5 text-sm"
      style={{
        left: anchorX + offsetX,
        top: anchorY + offsetY,
        transform,
      }}
    >
      {label && <div className="font-semibold mb-1.25">{label}</div>}

      <div>
        {items?.map((it, idx) => (
          <div key={`${it.datasetIndex}-${it.dataIndex}-${idx}`} className="flex justify-between items-center">
            <div className="flex items-center min-w-0 space-x-2.5">
              <span
                aria-hidden
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{
                  background: it.color,
                }}
              />
              <span className="truncate">{it.datasetLabel}</span>
            </div>

            <div className="font-semibold">{valueFormatter(it.raw)}</div>
          </div>
        ))}
      </div>

      <ChartTooltipArrow side={attachSide} />
    </div>
  );
}

function getValueFormatter(metric, defaultCurrency) {
  if (metric === METRICS.CONVERSION_RATE) {
    return (value) => (value == null ? '' : `${round(value, 1)}%`);
  }
  if (metric === METRICS.AVG_VALUE || metric === METRICS.TOTAL_VALUE) {
    return (value) => (value == null ? '' : formatCurrency(value, defaultCurrency));
  }
  return (value) => {
    if (value == null) return '';
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return '';
    return numeric.toLocaleString();
  };
}
